'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, randomString } from '@/lib/client-utils';
import { brandFromKey } from '@/lib/brand';
import styles from '../styles/Home.module.css';

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const tabIndex = searchParams?.get('tab') === 'custom' ? 1 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tab = index === 1 ? 'custom' : 'demo';
    router.push(`/?tab=${tab}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className="lk-button"
        onClick={() => {
          if (onTabSelected) {
            onTabSelected(index);
          }
        }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabSelect}>{tabs}</div>
      {/* @ts-ignore */}
      {props.children[tabIndex]}
    </div>
  );
}

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Marken-Chrome auch auf der Landing (18.07.2026): der Cockpit-Launcher-Fallback
  // führt hierher — mit ?brand=iu darf hier nichts mehr „Eva"/„Salon" heißen.
  const brand = brandFromKey(searchParams?.get('brand'));
  const wer = brand.agentFallbackName;
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  // Eva-Modus bewusst waehlbar (Footgun-Schutz): ohne Auswahl liefe still der
  // teure, sprechende Default. Cockpit-Links setzen ?im=… → vorausgewaehlt.
  const [evaMode, setEvaMode] = useState<'listen_speak' | 'listen_only'>(
    searchParams?.get('im') === 'listen_only' ? 'listen_only' : 'listen_speak',
  );
  // Protokoll-Versand pro Konferenz (Default AUS → Test-Konferenzen mailen nicht).
  const [emailProtocol, setEmailProtocol] = useState(searchParams?.get('email') === '1');
  const startMeeting = async () => {
    // Host: Raum registrieren; hostSecret in localStorage (Welle 5: statt sessionStorage →
    // überlebt Tab-Schließen/Reload; die Raum-Seite PageClientImpl liest denselben Key aus
    // localStorage — sessionStorage wäre dort unsichtbar und der Host landete im eigenen
    // Warteraum). NIE in der URL → kein Log-/Referer-Leak. Gast-Link ist schlicht /rooms/<id>.
    // Den Raumnamen wählt der SERVER (action=create, Sicherheits-Review MEET-01/02):
    // einen fremden Namen kann so niemand als Gastgeber belegen. Ohne Registrierung
    // gibt der Server kein Token mehr aus (MEET-04) — deshalb bei einem Fehler hier
    // stehen bleiben und es sagen, statt in einen toten Raum zu navigieren.
    const hostSecret = randomString(24);
    let roomName: string | null = null;
    let fehler = 'Der Raum konnte nicht angelegt werden — bitte erneut versuchen.';
    try {
      const r = await fetch('/api/waiting', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'create', hostSecret }),
      });
      if (r.ok) roomName = ((await r.json()) as { roomName?: string }).roomName ?? null;
      else if (r.status === 429)
        fehler = 'Zu viele neue Räume in kurzer Zeit — bitte gleich noch einmal.';
    } catch {
      /* unten gemeldet */
    }
    if (!roomName) {
      window.alert(fehler);
      return;
    }
    try {
      localStorage.setItem('eva-host:' + roomName, hostSecret);
    } catch {
      /* ohne localStorage ist die Gastgeber-Rolle nach einem Reload weg — wie bisher */
    }
    // Modus/Lernen (vom Cockpit-Launcher in der Landing-URL) an den Raum durchreichen →
    // meet_main liest sie aus der Eva-Dispatch-Metadata. Default ohne Query = Worker-Default.
    const q = new URLSearchParams();
    // Modus explizit aus der bewussten Auswahl (kein stiller listen_speak-Default
    // mehr → der Worker bekommt immer eindeutige Dispatch-Metadata).
    q.set('im', evaMode);
    const learning = searchParams?.get('learning');
    if (learning) q.set('learning', learning);
    // Protokoll-Versand pro Konferenz (nur wenn angehakt) → Eva-Dispatch-Metadata.
    if (emailProtocol) q.set('email', '1');
    // Ablauf-Engine Welle 1: Ad-hoc-Salon mit Ablauf-Vorlage (?script=<id> aus dem
    // Cockpit-Link) → der Worker holt die Vorlage direkt aus agent-pm.
    const script = searchParams?.get('script');
    if (script) q.set('script', script);
    // Voice-Agent-ID (04.08.2026): ?va= aus dem Cockpit-Launcher MUSS die
    // Navigation zum Raum überleben — der Worker attribuiert damit Ledger/
    // Memory/Lern-KB (vorher lief jeder Ad-hoc-Call als Eva/158).
    const va = searchParams?.get('va');
    if (va) q.set('va', va);
    // Launcher-Ticket des Cockpits über script/va (MEET-03) — ohne es übernimmt
    // der Server die beiden Werte nicht.
    const lt = searchParams?.get('lt');
    if (lt) q.set('lt', lt);
    // Marken-Chrome (18.07.2026): ?brand= MUSS die Navigation zum Raum überleben —
    // vorher verwarf startMeeting den Param und der IU-Launcher-Weg landete im
    // E-V-A-Chrome (User-Befund „überall noch Eva").
    const brand = searchParams?.get('brand');
    if (brand) q.set('brand', brand);
    const qs = q.toString() ? `?${q.toString()}` : '';
    if (e2ee) {
      router.push(`/rooms/${roomName}${qs}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${roomName}${qs}`);
    }
  };
  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0 }}>
        Starte einen neuen {brand.raumWort}-Raum — {wer} tritt automatisch bei.
      </p>
      <fieldset
        style={{
          marginTop: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '8px',
          padding: '0.6rem 1rem 0.8rem',
        }}
      >
        <legend style={{ padding: '0 0.4rem', fontSize: '0.85rem', opacity: 0.85 }}>
          {brand.key === 'eva' ? 'Eva im Salon' : `${wer} in der ${brand.raumWort}`}
        </legend>
        <label
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', cursor: 'pointer' }}
        >
          <input
            type="radio"
            name="eva-mode"
            checked={evaMode === 'listen_speak'}
            onChange={() => setEvaMode('listen_speak')}
          />
          <span>
            Hören + sprechen
            <span style={{ opacity: 0.6, fontSize: '0.8rem' }}>
              {' '}
              · {wer} spricht aktiv mit (höhere Live-Kosten)
            </span>
          </span>
        </label>
        <label
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'baseline',
            cursor: 'pointer',
            marginTop: '0.4rem',
          }}
        >
          <input
            type="radio"
            name="eva-mode"
            checked={evaMode === 'listen_only'}
            onChange={() => setEvaMode('listen_only')}
          />
          <span>
            Nur zuhören
            <span style={{ opacity: 0.6, fontSize: '0.8rem' }}>
              {' '}
              · {wer} bleibt still, transkribiert nur (~0,40&nbsp;$/h)
            </span>
          </span>
        </label>
      </fieldset>
      <label
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'baseline',
          cursor: 'pointer',
          marginTop: '0.8rem',
        }}
      >
        <input
          type="checkbox"
          checked={emailProtocol}
          onChange={(ev) => setEmailProtocol(ev.target.checked)}
        />
        <span>
          📧 Protokoll nach der Konferenz an die Teilnehmer senden
          <span style={{ opacity: 0.6, fontSize: '0.8rem' }}>
            {' '}
            · {wer} mailt das Ergebnisprotokoll automatisch (sonst nur Ablage in Drive)
          </span>
        </span>
      </label>
      <button style={{ marginTop: '1rem' }} className="lk-button" onClick={startMeeting}>
        {brand.startLabel}
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">Ende-zu-Ende-Verschlüsselung aktivieren</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CustomConnectionTab(props: { label: string }) {
  const router = useRouter();

  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const serverUrl = formData.get('serverUrl');
    const token = formData.get('token');
    if (e2ee) {
      router.push(
        `/custom/?liveKitUrl=${serverUrl}&token=${token}#${encodePassphrase(sharedPassphrase)}`,
      );
    } else {
      router.push(`/custom/?liveKitUrl=${serverUrl}&token=${token}`);
    }
  };
  return (
    <form className={styles.tabContent} onSubmit={onSubmit}>
      <p style={{ marginTop: 0 }}>
        Connect LiveKit Meet with a custom server using LiveKit Cloud or LiveKit Server.
      </p>
      <input
        id="serverUrl"
        name="serverUrl"
        type="url"
        placeholder="LiveKit Server URL: wss://*.livekit.cloud"
        required
      />
      <textarea
        id="token"
        name="token"
        placeholder="Token"
        required
        rows={5}
        style={{ padding: '1px 2px', fontSize: 'inherit', lineHeight: 'inherit' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">Ende-zu-Ende-Verschlüsselung aktivieren</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>

      <hr
        style={{ width: '100%', borderColor: 'rgba(255, 255, 255, 0.15)', marginBlock: '1rem' }}
      />
      <button
        style={{ paddingInline: '1.25rem', width: '100%' }}
        className="lk-button"
        type="submit"
      >
        Connect
      </button>
    </form>
  );
}

function LandingShell() {
  // Marken-Chrome der Landing (18.07.2026): Wortmarke/Untertitel/Footer + die
  // CSS-Variablen-Overrides folgen ?brand= (Launcher-Fallback-Weg). Default
  // eva = unverändertes Ist-Verhalten.
  const searchParams = useSearchParams();
  const brand = brandFromKey(searchParams?.get('brand'));
  return (
    <>
      <main className={styles.main} data-lk-theme="default" style={{ ...(brand.theme || {}) }}>
        <div className="header">
          <h1 className="wordmark">{brand.appName}</h1>
          <h2>{brand.landingLine}</h2>
        </div>
        <Tabs>
          <DemoMeetingTab label={brand.key === 'eva' ? 'Neuer Salon' : 'Neue Konferenz'} />
          <CustomConnectionTab label="Mit Token" />
        </Tabs>
      </main>
      <footer data-lk-theme="default" style={{ ...(brand.theme || {}) }}>
        {brand.footerLine}
      </footer>
    </>
  );
}

export default function Page() {
  return (
    <Suspense fallback="Loading">
      <LandingShell />
    </Suspense>
  );
}
